import axiosInstance from '../api';

/**
 * Market trend: the last 90 days against the same 90 days a year earlier.
 */
export interface MarketTrend {
  direction: 'improving_for_buyers' | 'stable' | 'improving_for_sellers';
  salesChange: number; // % change in closed sales vs. a year ago
  daysOnMarketChange: number; // % change in average DOM vs. a year ago
  priceDropChange: number; // Change in % of listings with price drops
  periodWeeks: number; // How many weeks of data analyzed
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Which geography the figures describe.
 *
 * - `radius` -- every listing within N miles of the property, the same
 *   population the comps engine draws from. What a read about one home means
 *   by "the market", since a municipal boundary can be far coarser than a ZIP.
 * - `neighborhood` -- the curated neighborhood the property sits in, the same
 *   geolocated listing set the neighborhood guide for that area publishes.
 * - `zip` -- the raw ZIP, and the fallback for both of the above.
 */
export interface MarketDataArea {
  kind: 'neighborhood' | 'zip' | 'radius';
  slug: string | null;
  /** Display name: "within 3 miles", "Athens", or "35613". */
  name: string;
  /** Boundary credit line, printed under figures derived from a polygon. */
  attribution: string | null;
  matchedBy?: string;
  /** The radius that answered, after any widening. Radius scope only. */
  radiusMiles?: number;
}

export interface MarketData {
  // Core inventory
  activeListings: number;
  monthsOfInventory: number;
  marketCondition: string;

  // Activity (weekly figures)
  weeklySales: number;
  weeklySalesAvg: number;
  weeklyNewListings: number;
  underContractPercent: number;

  // 90-day activity
  soldListings: number;

  // Timing
  medianDaysOnMarket: number;
  medianDaysToContract: number;
  medianDaysToClose: number;
  /**
   * Median days-on-market of SOLD listings only -- what "Typical time to
   * sell" must read against. `medianDaysOnMarket` above mixes in active
   * listings, so it is never the right number for that copy.
   */
  medianSoldDaysOnMarket?: number;
  soldDaysOnMarketP25?: number;
  soldDaysOnMarketP75?: number;

  // Pricing
  medianListPrice: number;
  medianSoldPrice: number;
  medianPricePerSqFt: number;
  medianSoldPricePerSqFt: number;
  listToSaleRatio: number;

  // Market signals
  priceDropPercent: number;

  // Trend analysis (from 3 months of timeseries data)
  trend: MarketTrend | null;

  // Metadata
  propertyType: string;
  dataDate: string | null;
  /** Optional: responses cached before neighborhood scoping shipped lack it. */
  area?: MarketDataArea;

  // Optional buyer analysis (when includeBuyerAnalysis=true)
  buyerAnalysis?: BuyerAnalysis;
}

/**
 * Buyer-specific analysis for a property
 */
export interface BuyerInsight {
  type: 'positive' | 'caution' | 'neutral';
  text: string;
  tip: string;
  factor?: string;
}

export interface OfferRangeCalculation {
  low: number;
  suggested: number;
  high: number;
  lowPercent: number;
  suggestedPercent: number;
  highPercent: number;
}

export interface BargainingScoreFactor {
  name: string;
  score: number;
  maxScore: number;
  description: string;
}

export interface BargainingScore {
  score: number;
  confidence: 'high' | 'medium' | 'low';
  factors: BargainingScoreFactor[];
}

export interface BuyerAnalysis {
  insights: BuyerInsight[];
  offerRecommendation: string;
  actionTip: string;
  offerRange: string;
  offerRangeCalculation?: OfferRangeCalculation;
  nextSteps: string[];
  bargainingScore?: BargainingScore;
  /**
   * Status awareness (additive -- consumers written before this existed can
   * still ignore these). `offerAdviceAvailable` is false for anything that
   * is not actively for sale (SOLD, EXPIRED, WITHDRAWN, PENDING); `offerRange`
   * and `offerRangeCalculation` are empty/absent in that case too.
   */
  listingStatus?: 'ACTIVE' | 'PENDING' | 'SOLD' | 'EXPIRED' | 'WITHDRAWN' | 'UNKNOWN';
  offerAdviceAvailable?: boolean;
  statusNote?: string | null;
}

const marketDataService = {
  find: async ({
    params,
  }: {
    params: {
      zip?: string;
      city?: string;
      state?: string;
      /** Coordinates let the API scope the stats to a radius or neighborhood. */
      lat?: number;
      lng?: number;
      mlsAreaMinor?: string;
      subdivision?: string;
      /**
       * 'radius' measures from lat/lng outward, widening until the area holds
       * enough activity; 'zip' opts out of any scoping.
       */
      scope?: 'auto' | 'zip' | 'radius';
      /** Starting radius for 'radius' scope. Defaults to 3 miles. */
      radiusMiles?: number;
      /** MLS property-type enum ('SFR', 'CONDO', ...) to restrict rows to. */
      mlsPropertyType?: string;
      propertyType?: string;
      timeframe?: number;
      includeBuyerAnalysis?: boolean;
      dom?: number;
      listingPrice?: number;
      area?: number;
      /**
       * MLS `isActive`/status of the specific listing this request's
       * dom/listingPrice/area describe (e.g. 'ACTIVE', 'SOLD', 'PENDING').
       * Makes `buyerAnalysis` status-aware -- a SOLD/PENDING listing gets
       * `offerAdviceAvailable: false` instead of an ACTIVE listing's offer
       * range.
       */
      status?: string;
    };
  }): Promise<MarketData> => {
    const response = await axiosInstance.get('/market-data', {
      params,
    });
    return response.data;
  },
};

export default marketDataService;
